import useMediaQuery from '@mui/material/useMediaQuery';
import { useEffect, useRef, useState } from 'react';

// Each streaming frame reveals at least one character plus ~1/8 of the remaining backlog, so
// a large coalesced delta flush catches up quickly without ever lagging the incoming stream.
const BACKLOG_REVEAL_DIVISOR = 8;

export default function useSmoothedText(text: string, streaming: boolean) {
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const revealsGradually = streaming && !prefersReducedMotion;
  const [shown, setShown] = useState(revealsGradually ? '' : text);
  const shownLengthRef = useRef(shown.length);
  const frameRef = useRef<number>();

  useEffect(() => {
    if (!revealsGradually) {
      shownLengthRef.current = text.length;
      setShown(text);

      return;
    }

    const revealNextFrame = () => {
      const current = shownLengthRef.current;

      if (current >= text.length) {
        return;
      }

      const next = Math.min(
        text.length,
        current +
          Math.max(1, Math.ceil((text.length - current) / BACKLOG_REVEAL_DIVISOR)),
      );
      shownLengthRef.current = next;
      setShown(text.slice(0, next));

      if (next < text.length) {
        frameRef.current = requestAnimationFrame(revealNextFrame);
      }
    };

    frameRef.current = requestAnimationFrame(revealNextFrame);

    return () => {
      if (frameRef.current) {
        cancelAnimationFrame(frameRef.current);
      }
    };
  }, [text, revealsGradually]);

  return shown;
}
